export const formatUserName = (name?: string): string => {
  if (!name) return '-';
  if (name.length <= 8) return name;

  const splitted = name.split(' ');

  if (splitted.length === 1) {
    return `${splitted[0].slice(0, 8)}..`;
  }

  const firstToRestWord = splitted.slice(0, -1).join(' ');

  const firstWord =
    firstToRestWord.length > 8
      ? `${firstToRestWord.slice(0, 8)}..`
      : firstToRestWord;

  const lastWord = splitted[splitted.length - 1];

  return `${firstWord} ${lastWord.slice(0, 1)}`;
};

export const getInititalName = (
  existing: string[],
  fullName?: string,
  maxLength = 3,
): string => {
  if (!fullName) return '';

  // Split the full name into an array of words
  const words = fullName.split(' ');

  // Initialize an empty string to store the initials
  let initials = '';

  // Iterate over each word in the array

  for (const word of words) {
    initials += word.charAt(0).toUpperCase();
  }

  if (!existing?.length || !existing.includes(initials)) return initials;

  return getInitialsByConsonant(existing, fullName, maxLength);
};

export const getInitialsByConsonant = (
  existingInitials: string[],
  fullName: string,
  maxLength: number,
): string => {
  if (!fullName) return '';

  const words = fullName.split(' ');
  let initials = '';

  const getInitial = (): void => {
    for (const word of words) {
      const consonants = word.match(/[bcdfghjklmnpqrstvwxyz]/gi);
      if (consonants && consonants.length > 0 && initials.length < maxLength) {
        const currentLength = initials.length;
        const remaining = maxLength - currentLength;

        initials += consonants.slice(0, remaining).join('');
      }
    }
  };

  Array.from({ length: words.length }).forEach(() => {
    getInitial();
    initials = initials.toUpperCase();

    if (existingInitials.includes(initials) && words.length != 1) {
      words.splice(0, 1);
      initials = '';
    }
  });

  return getInitialsBy3FirstLetter(existingInitials, fullName, maxLength);
};

export const getInitialsBy3FirstLetter = (
  existingInitials: string[],
  fullName: string,
  maxLength: number,
): string => {
  if (!fullName) return '';

  const words = fullName.split(' ');
  let initials = '';

  const getInitial = (word: string): string => {
    return word.slice(0, maxLength).toUpperCase();
  };

  for (const word of words) {
    initials = getInitial(word);

    if (!existingInitials.includes(initials)) {
      break;
    }
  }

  return initials;
};

/**
 * Formats the label with the appropriate article ("a" or "an") based on its vowel sound.
 * This function checks if the label starts with a vowel or has a special case for silent "h" words.
 * It ensures that the correct article ("a" or "an") is used according to English pronunciation rules.
 *
 * For example:
 * - "hour" will be prefixed with "an" (due to the silent "h").
 * - "unicorn" will be prefixed with "a" (due to the "u" sound).
 *
 * @param {string} [label] - The label to be formatted. If no label is provided, returns an empty string.
 * @returns {string} The label prefixed with the appropriate article ("a" or "an").
 */
export const formatVowelSoundLabel = (label?: string): string => {
  if (!label) return '';

  const vowelSound = ['a', 'e', 'i', 'o', 'h'];
  const vocalSound = ['a', 'e', 'i', 'o', 'u'];
  const word = label.toLowerCase();
  const firstLetter = word[0];
  const isStartWithVowel = vowelSound.includes(firstLetter);

  if (firstLetter === 'h') {
    const silent = ['hour', 'honest', 'heir', 'honor'];
    const includeSilent = silent.some((phrase) => word.includes(phrase));

    if (!vocalSound.includes(word.slice(2, 3)) && !includeSilent)
      return `a ${label}`;
    return `an ${label}`;
  }

  return isStartWithVowel ||
    (firstLetter === 'u' && !vowelSound.includes(word.slice(2, 3)))
    ? `an ${label}`
    : `a ${label}`;
};
